import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { usePlausible } from "@/hooks/usePlausible";

type Answer = "ja" | "teilweise" | "nein" | "";

interface Answers {
  q1: Answer;
  q2: Answer;
  q3: Answer;
  q4: Answer;
  q5: Answer;
}

const QUESTIONS = [
  {
    key: "q1" as const,
    text: "Bieten Sie an Ladestationen ≥ 50 kW eine Ad-hoc-Zahlungsmöglichkeit (Kreditkarte oder kontaktloses Bezahlen) gemäß AFIR Art. 5 an?",
  },
  {
    key: "q2" as const,
    text: "Sind alle Ladepunkte mit eichrechtskonformen Zählern gemäß MessEG/MessEV ausgestattet?",
  },
  {
    key: "q3" as const,
    text: "Sind LSV-konforme Stecker- und Kennzeichnungs-Standards an jedem Standort umgesetzt?",
  },
  {
    key: "q4" as const,
    text: "Liegt für jede Ladestation eine BDEW-konforme Netzanschluss-Bescheinigung vor?",
  },
  {
    key: "q5" as const,
    text: "Existiert ein Update-Mechanismus mit Schwachstellen-Meldepfad gemäß CRA-Anforderungen?",
  },
];

const SCORE_MAP: Record<"ja" | "teilweise" | "nein", number> = {
  ja: 20,
  teilweise: 10,
  nein: 0,
};

function calcScore(answers: Answers): number {
  return (["q1", "q2", "q3", "q4", "q5"] as const).reduce((sum, key) => {
    const a = answers[key];
    return a ? sum + SCORE_MAP[a as "ja" | "teilweise" | "nein"] : sum;
  }, 0);
}

function interpretation(score: number): string {
  if (score >= 80) {
    return "Ihre Ladeinfrastruktur erfüllt die wesentlichen regulatorischen Anforderungen. Dennoch empfehlen wir eine vollständige Prüfung, da einzelne Standortbesonderheiten und aktuelle Gesetzessänderungen im Detail abweichen können.";
  }
  if (score >= 50) {
    return "Ihre Infrastruktur weist partiellen Handlungsbedarf auf. Einzelne Anforderungen aus AFIR, Eichrecht oder CRA sind noch nicht vollständig umgesetzt — konkrete Maßnahmen helfen, Bußgeldrisiken zu reduzieren.";
  }
  return "Es bestehen erhebliche Compliance-Lücken mit potenziell signifikantem Bußgeldrisiko. Wir empfehlen dringend eine strukturierte Analyse und priorisierte Umsetzung der fehlenden Maßnahmen.";
}

function scoreColor(score: number): string {
  if (score >= 80) return "var(--color-primary)";      // burgundy
  if (score >= 50) return "var(--color-accent)";       // deep gold
  return "var(--color-navy)";                           // anthracite
}

export function ComplianceScore() {
  const plausible = usePlausible();

  const [answers, setAnswers] = useState<Answers>({ q1: "", q2: "", q3: "", q4: "", q5: "" });
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [emailError, setEmailError] = useState("");

  const allAnswered = (["q1", "q2", "q3", "q4", "q5"] as const).every((k) => answers[k] !== "");

  function handleCalculate() {
    if (!allAnswered) return;
    setScore(calcScore(answers));
    setSubmitted(true);
  }

  async function handleEmailSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || emailStatus === "submitting") return;
    setEmailStatus("submitting");
    setEmailError("");
    try {
      const res = await fetch("/api/audit-request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          intent: "score",
          score,
          answers: { q1: answers.q1, q2: answers.q2, q3: answers.q3, q4: answers.q4, q5: answers.q5 },
          email: trimmed,
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setEmailError(data.error || "Anfrage konnte nicht gesendet werden.");
        setEmailStatus("error");
        return;
      }
      plausible("score_submitted");
      setEmailStatus("success");
      setEmail("");
    } catch {
      setEmailError("Netzwerkfehler. Bitte erneut versuchen.");
      setEmailStatus("error");
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] text-[var(--color-text-primary)]" style={{ background: "var(--color-surface)" }}>
      {/* Header */}
      <header className="border-b border-[var(--color-border)] bg-white px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="text-xl font-bold" style={{ letterSpacing: "-0.02em" }}>
            <span style={{ color: "var(--color-navy)" }}>Charge</span>
            <span style={{ color: "var(--color-primary)" }}>Suite</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm text-[var(--color-text-secondary)]">
            <Link to="/" className="transition-colors hover:text-[var(--color-text-primary)]">
              Zur Startseite
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16 sm:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl" style={{ color: "var(--color-navy)" }}>
            Compliance-Score in 60 Sekunden
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-[var(--color-text-secondary)]">
            5 Fragen. Sofortiger Score. Detaillierter Bericht per E-Mail.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="px-6 pb-20">
        <div className="mx-auto max-w-2xl">
          {!submitted ? (
            <div className="rounded-2xl border border-[var(--color-border)] bg-white p-8 shadow-sm">
              <div className="space-y-8">
                {QUESTIONS.map((q, idx) => (
                  <fieldset key={q.key} className="space-y-3">
                    <legend className="text-sm font-semibold leading-snug" style={{ color: "var(--color-navy)" }}>
                      <span
                        className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white"
                        style={{ background: "var(--color-primary)" }}
                      >
                        {idx + 1}
                      </span>
                      {q.text}
                    </legend>
                    <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                      {(["ja", "teilweise", "nein"] as const).map((opt) => {
                        const id = `${q.key}-${opt}`;
                        const checked = answers[q.key] === opt;
                        return (
                          <label
                            key={opt}
                            htmlFor={id}
                            className="flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-colors"
                            style={{
                              borderColor: checked ? "var(--color-primary)" : "var(--color-border)",
                              background: checked ? "var(--color-primary-soft, #F5EEF2)" : "white",
                              color: checked ? "var(--color-primary)" : "var(--color-text-primary)",
                            }}
                          >
                            <input
                              id={id}
                              type="radio"
                              name={q.key}
                              value={opt}
                              checked={checked}
                              onChange={() => setAnswers((prev) => ({ ...prev, [q.key]: opt }))}
                              className="sr-only"
                            />
                            <span
                              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px]"
                              style={{
                                borderColor: checked ? "var(--color-primary)" : "var(--color-border)",
                                background: checked ? "var(--color-primary)" : "white",
                              }}
                            >
                              {checked && <span className="block h-2 w-2 rounded-full bg-white" />}
                            </span>
                            <span className="font-medium capitalize">{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </div>

              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={handleCalculate}
                  disabled={!allAnswered}
                  className="rounded-xl px-10 py-3.5 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                  style={{ background: "var(--color-primary)" }}
                >
                  Score berechnen
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Score display */}
              <div
                className="rounded-2xl border bg-white p-8 text-center shadow-sm"
                style={{
                  borderColor: scoreColor(score),
                  borderWidth: "2px",
                  background: score < 50 ? "var(--color-surface-secondary, #F0EDEA)" : "white",
                }}
              >
                <p className="text-sm font-semibold uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>
                  Ihr Compliance-Score
                </p>
                <p
                  className="mt-3 text-8xl font-bold leading-none"
                  style={{ color: scoreColor(score) }}
                >
                  {score}
                </p>
                <p className="mt-1 text-2xl font-light text-[var(--color-text-secondary)]">/&thinsp;100</p>

                {score < 50 && (
                  <div
                    className="mt-4 rounded-lg px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                    style={{ background: "var(--color-navy)", color: "white" }}
                  >
                    Dringender Handlungsbedarf
                  </div>
                )}

                <p className="mx-auto mt-6 max-w-lg text-sm leading-relaxed text-[var(--color-text-secondary)]">
                  {interpretation(score)}
                </p>
              </div>

              {/* Answer summary */}
              <div className="rounded-2xl border border-[var(--color-border)] bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-sm font-bold uppercase tracking-wider" style={{ color: "var(--color-navy)" }}>
                  Ihre Antworten
                </h2>
                <ul className="space-y-3">
                  {QUESTIONS.map((q, idx) => {
                    const a = answers[q.key] as "ja" | "teilweise" | "nein";
                    return (
                      <li key={q.key} className="flex items-start gap-3 text-sm">
                        <span
                          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                          style={{ background: "var(--color-primary)" }}
                        >
                          {idx + 1}
                        </span>
                        <span className="flex-1 leading-snug text-[var(--color-text-secondary)]">{q.text}</span>
                        <span
                          className="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize"
                          style={{
                            background:
                              a === "ja"
                                ? "var(--color-compliance-pass)"
                                : a === "teilweise"
                                  ? "var(--color-accent)"
                                  : "var(--color-danger)",
                            color: "white",
                          }}
                        >
                          {a} (+{SCORE_MAP[a]})
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Email capture */}
              <div
                className="rounded-2xl p-8 text-center"
                style={{ background: "var(--color-navy)" }}
              >
                {emailStatus === "success" ? (
                  <p className="text-base font-medium text-white" role="status">
                    Vielen Dank — der Bericht wird binnen weniger Minuten an Ihre E-Mail-Adresse zugestellt.
                  </p>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-white">
                      Erhalten Sie den ausführlichen Bericht mit konkreten Maßnahmen per E-Mail.
                    </h2>
                    <p className="mt-3 text-sm" style={{ color: "#A89E95" }}>
                      Wir analysieren Ihre Antworten und senden Ihnen einen priorisierten Handlungsplan.
                    </p>
                    <form
                      onSubmit={handleEmailSubmit}
                      className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
                    >
                      <label htmlFor="score-email" className="sr-only">
                        E-Mail-Adresse
                      </label>
                      <input
                        id="score-email"
                        type="email"
                        required
                        disabled={emailStatus === "submitting"}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ihre@email.de"
                        className="w-full rounded-xl border border-white/20 bg-white/10 px-5 py-3.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-60 sm:w-80"
                      />
                      <button
                        type="submit"
                        disabled={emailStatus === "submitting"}
                        className="w-full rounded-xl px-7 py-3.5 text-sm font-semibold text-[var(--color-navy)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                        style={{ background: "white" }}
                      >
                        {emailStatus === "submitting" ? "Wird gesendet…" : "Bericht anfordern"}
                      </button>
                    </form>
                    {emailStatus === "error" && (
                      <p className="mt-4 text-sm font-medium text-amber-300" role="alert">
                        {emailError}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[var(--color-border)] bg-white px-6 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <span className="text-base font-bold" style={{ letterSpacing: "-0.02em" }}>
            <span style={{ color: "var(--color-navy)" }}>Charge</span>
            <span style={{ color: "var(--color-primary)" }}>Suite</span>
          </span>
          <nav className="flex flex-wrap items-center justify-center gap-6 text-sm text-[var(--color-text-secondary)]">
            <Link to="/impressum" className="transition-colors hover:text-[var(--color-text-primary)]">
              Impressum
            </Link>
            <Link to="/privacy" className="transition-colors hover:text-[var(--color-text-primary)]">
              Datenschutz
            </Link>
            <Link to="/terms" className="transition-colors hover:text-[var(--color-text-primary)]">
              AGB
            </Link>
          </nav>
          <p className="text-sm text-[var(--color-text-muted)]">ChargeSuite 2026. Made in Germany.</p>
        </div>
      </footer>
    </div>
  );
}
